import { IndustrialCaseAudioPlayer } from './IndustrialCaseAudioPlayer';
import { IndustrialCaseMediaCaption } from './IndustrialCaseMediaCaption';
import { IndustrialCaseVideoPlayer } from './IndustrialCaseVideoPlayer';
import { parseIndustrialCaseRichText } from './parseIndustrialCaseRichText';

type IndustrialCaseRichTextProps = {
  /**
   * ### className
   *
   * Public article typography and media spacing classes.
   */
  className: string;
  /**
   * ### html
   *
   * Authored and citation-resolved narrative HTML.
   */
  html: string;
};

/**
 * ### IndustrialCaseRichText
 *
 * Preserves CKEditor prose while replacing authored media figures with React
 * players during the initial render. Rendering one ordered tree keeps preview
 * and public layouts identical without post-hydration DOM mutation.
 *
 * @param props Authored HTML and its article class names
 * @returns Rich narrative content with hydrated audio and video blocks
 */
export function IndustrialCaseRichText({
  className,
  html,
}: IndustrialCaseRichTextProps) {
  const segments = parseIndustrialCaseRichText(html);

  return (
    <div className={className}>
      {segments.map((segment) => {
        if (segment.type === 'html') {
          return (
            <div
              className="contents"
              dangerouslySetInnerHTML={{ __html: segment.html }}
              key={segment.key}
            />
          );
        }

        if (!isSafeMediaSource(segment.src)) {
          return (
            <figure
              className={`case-media case-media--${segment.mediaKind}`}
              key={segment.key}
            >
              <p
                className="rounded-[2px] border border-[#d8d8d3] bg-[#f5f5f2] px-5 py-6 text-sm font-medium text-[#686864]"
                role="status"
              >
                미디어 주소를 확인해 주세요
              </p>
              <IndustrialCaseMediaCaption caption={segment.caption} />
            </figure>
          );
        }

        return segment.mediaKind === 'audio' ? (
          <IndustrialCaseAudioPlayer
            caption={segment.caption}
            key={segment.key}
            src={segment.src}
          />
        ) : (
          <IndustrialCaseVideoPlayer
            caption={segment.caption}
            key={segment.key}
            src={segment.src}
          />
        );
      })}
    </div>
  );
}

function isSafeMediaSource(src: string) {
  if (!src) return false;

  try {
    const url = new URL(src, 'https://deeply.invalid');
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}
